import type { SwarmType } from '@/graphql/types';
import { SwarmTypeTranslations } from '../../../enums';
import { isSwarmType } from './typeGuards';

// `null` is the explicit, persisted representation for content that supports every
// current and future SWARM type. A missing value is never treated as generic.
export type SupportedSwarmTypes = readonly SwarmType[] | null;

export function assertSupportedSwarmTypes(
    value: unknown,
    path: string
): asserts value is SupportedSwarmTypes {
    if (value === null) {
        return;
    }

    if (!Array.isArray(value) || value.length === 0) {
        throw new Error(`Expected "${path}" to be null or a non-empty SWARM type list.`);
    }

    const seen = new Set<SwarmType>();
    value.forEach((entry, index) => {
        if (!isSwarmType(entry)) {
            throw new Error(`Expected "${path}[${index}]" to be a valid SWARM type.`);
        }
        if (seen.has(entry)) {
            throw new Error(`Expected "${path}" to contain unique SWARM types.`);
        }
        seen.add(entry);
    });
}

export function supportsSwarmType(
    supportedSwarmTypes: SupportedSwarmTypes,
    swarmType: SwarmType
): boolean {
    return supportedSwarmTypes === null || supportedSwarmTypes.includes(swarmType);
}

export function formatSwarmType(swarmType: SwarmType): string {
    return SwarmTypeTranslations[swarmType];
}

export function formatSupportedSwarmTypes(supportedSwarmTypes: SupportedSwarmTypes): string {
    if (supportedSwarmTypes === null) {
        return gettext('ALL_SENSOR_TYPES');
    }

    return supportedSwarmTypes.map(formatSwarmType).join(', ');
}
